import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, LogIn, X } from "lucide-react";
import { startLogin } from "@/lib/auth";

/**
 * Shown when an unauthenticated visitor tries to trigger an action that
 * requires login (e.g. clicking "Analyze track"). Explains why and prompts
 * for Google sign-in. Very light-weight, no full-screen block.
 */
export default function LoginPrompt({ onClose }) {
  const { t } = useTranslation();

  const handleLogin = async () => {
    try {
      await startLogin();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("auth.sign_in_error"));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="login-prompt-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[color:var(--bg-elev)] p-7 shadow-2xl"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[color:var(--fg-muted)] hover:text-white transition-colors"
          aria-label={t("consent.close")}
          data-testid="login-prompt-close"
        >
          <X size={18} />
        </button>

        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
          <Sparkles size={22} className="text-white" />
        </div>

        <h2 className="font-display text-2xl font-bold text-white mb-2">
          {t("auth.prompt_title")}
        </h2>
        <p className="text-sm text-[color:var(--fg-secondary)] leading-relaxed mb-5">
          {t("auth.prompt_body")}
        </p>

        <ul className="text-xs text-[color:var(--fg-secondary)] space-y-1.5 mb-6">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-[color:var(--brand)] shrink-0" />
            <span>{t("auth.prompt_bullet_1")}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-[color:var(--brand)] shrink-0" />
            <span>{t("auth.prompt_bullet_2")}</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-[color:var(--brand)] shrink-0" />
            <span>{t("auth.prompt_bullet_3")}</span>
          </li>
        </ul>

        <button
          onClick={handleLogin}
          className="btn-primary w-full flex items-center justify-center gap-2"
          data-testid="login-google-btn"
        >
          <LogIn size={16} />
          <span>{t("auth.sign_in_google")}</span>
        </button>

        {/* Browsewrap-style legal notice (reinforces the explicit consent
            collected later by ConsentModal on first upload). Renders the
            translation with two inline <a> links via i18next Trans. */}
        <p
          className="text-[11px] text-[color:var(--fg-muted)] text-center mt-3 leading-snug"
          data-testid="login-legal-notice"
        >
          <Trans
            i18nKey="auth.prompt_legal_notice"
            components={{
              tos: (
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white transition-colors"
                />
              ),
              privacy: (
                <a
                  href="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white transition-colors"
                />
              ),
            }}
          />
        </p>

        <div className="text-[10px] text-[color:var(--fg-muted)] text-center mt-4">
          {t("auth.prompt_footer")}
        </div>
      </div>
    </div>
  );
}
